import { useEffect, useRef } from 'react';

import { useAnnouncer, type Politeness } from './announcerContext';

/**
 * Announces `message` whenever it changes to a new non-empty value.
 *
 * The common case: an async query settles and the screen should say "Loaded" or
 * "Couldn't load pricing". Doing that inline in a render is wrong (renders are
 * not events and can repeat), and doing it in the query's onSuccess is wrong too
 * (that callback is gone in TanStack Query v5). Deriving it from state with a
 * change guard is the version that fires exactly once per real transition.
 */
export function useAnnounceOnChange(
  message: string | null | undefined,
  politeness: Politeness = 'polite',
): void {
  const { announce } = useAnnouncer();
  const previous = useRef<string | null>(null);

  useEffect(() => {
    const next = message?.trim() ?? '';
    if (!next || next === previous.current) return;
    previous.current = next;
    announce(next, politeness);
  }, [message, politeness, announce]);
}
